import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { PermissionGuard } from './core/auth/guards/permission.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    {path: '', pathMatch : 'full', redirectTo: 'dashboard'},

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'dashboard'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'confirmation-success', loadChildren: () => import('app/modules/auth/confirmation-success/confirmation-success.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')},
            {path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes')}
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')}
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'dashboard', loadChildren: () => import('app/modules/admin/pages/dashboard/analytics/analytics.routes')},
            {path: 'links', loadChildren: () => import('app/modules/admin/pages/links/links.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'READ_SHORTENED_LINKS'}},

            {path: 'users', loadChildren: () => import('app/modules/admin/pages/users/users.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'LIST_USERS'}},
            {path: 'users/read/:id', loadChildren: () => import('app/modules/admin/pages/users/read/read.routes')},
            {path: 'users/edit/:id', loadChildren: () => import('app/modules/admin/pages/users/edit/edit.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'EDIT_USERS'}},
            {path: 'users/create', loadChildren: () => import('app/modules/admin/pages/users/edit/edit.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'EDIT_USERS'}},
            {path: 'users/me/read', loadChildren: () => import('app/modules/admin/pages/users/read/read.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'DETAIL_USERS'}},
            {path: 'users/me/edit', loadChildren: () => import('app/modules/admin/pages/users/edit/edit.routes')},            
            {path: 'users/invite', loadChildren: () => import('app/modules/admin/pages/users/invite/invite.routes')},            

            {path: 'statistics', loadChildren: () => import('app/modules/admin/pages/statistics/dashboard/statistics.routes')},
            {path: 'payments', loadChildren: () => import('app/modules/admin/pages/payments/payments.routes')},
            {path: 'campaigns', loadChildren: () => import('app/modules/admin/pages/campaigns/campaigns.routes')},

            {path: 'ads', loadChildren: () => import('app/modules/admin/pages/ads/ads.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'READ_ADS'}},
            {path: 'ad/read/:id', loadChildren: () => import('app/modules/admin/pages/ads/read-view/read-view.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'READ_ADS'}},
            {path: 'ad/edit/:id', loadChildren: () => import('app/modules/admin/pages/ads/add-edit-view/add-edit-view.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'WRITE_ADS'}},
            {path: 'ad/create', loadChildren: () => import('app/modules/admin/pages/ads/add-edit-view/add-edit-view.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'WRITE_ADS'}},
            
            {path: 'faq', loadChildren: () => import('app/modules/admin/pages/faq/faq.routes')},
            {path: 'support', loadChildren: () => import('app/modules/admin/pages/support/support.routes')},

            {path: 'compliance', loadChildren: () => import('app/modules/admin/pages/compliances/compliances.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'READ_COMPLIANCE'}},
            {path: 'compliance/read/:id', loadChildren: () => import('app/modules/admin/pages/compliances/read-view/read-view.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'READ_COMPLIANCE'}},
            {path: 'compliance/edit/:id', loadChildren: () => import('app/modules/admin/pages/compliances/add-edit-view/add-edit-view.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'WRITE_COMPLIANCE'}},
            {path: 'compliance/create', loadChildren: () => import('app/modules/admin/pages/compliances/add-edit-view/add-edit-view.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'WRITE_COMPLIANCE'}},

            {path: 'brokenlinks', loadChildren: () => import('app/modules/admin/pages/brokenlinks/brokenlinks.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'SEARCH_SHORTENED_LINKS'}},
            {path: 'brokenlinks/read/:id', loadChildren: () => import('app/modules/admin/pages/brokenlinks/read-view/read-view.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'SEARCH_SHORTENED_LINKS'}},

            {path: 'searchlinks', loadChildren: () => import('app/modules/admin/pages/searchlinks/searchlinks.routes'), canActivate: [PermissionGuard], data: { requiredPermission: 'SEARCH_SHORTENED_LINKS'}},
        ]
    },
    {path: '**', pathMatch : 'full', redirectTo: ''},

];
